// frontend/src/components/Auth/Login.js
import React, { useState, useContext, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Link,
  Box
} from '@mui/material';
import { toast } from 'react-toastify';
import { loginUser, logoutUser } from '../../services/firebaseAuth';
import api from '../../services/api';
import API from '../config';

export default function Login() {
  const navigate = useNavigate();
  const { authData } = useContext(AuthContext);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(false);

  // Navigate to dashboard when authData becomes available after login
  useEffect(() => {
    if (pendingNavigation && authData?.access_token) {
      setPendingNavigation(false);
      navigate('/dashboard');
    }
  }, [authData, pendingNavigation, navigate]);

  const handleChange = e => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const user = await loginUser(formData.email, formData.password);
      
      // Get the Firebase token to check if user is blocked
      const token = await user.getIdToken();
      
      // Check if user account is blocked before proceeding - MUST complete before navigation
      let isBlocked = false;
      let blockedErrorMessage = null;
      
      try {
        const response = await api.get(`${API}/api/firebase-auth/check-blocked`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Explicitly check the response
        if (response.data?.blocked === true) {
          isBlocked = true;
          blockedErrorMessage = response.data?.error || 'Your account has been blocked. If you have any questions, please contact support at admin@onlystatsvip.com';
        }
      } catch (blockedError) {
        // Check if account is blocked
        if (blockedError.response?.status === 403) {
          isBlocked = true;
          // Try to get error message from response data
          blockedErrorMessage = blockedError.response?.data?.error || 
                                blockedError.response?.data?.message ||
                                'Your account has been blocked. If you have any questions, please contact support at admin@onlystatsvip.com';
        }
        // If it's a network error or other issue, still check the response data
        else if (blockedError.response?.data?.blocked === true || 
            (blockedError.response?.data?.error && blockedError.response.data.error.toLowerCase().includes('blocked'))) {
          isBlocked = true;
          blockedErrorMessage = blockedError.response.data.error || 'Your account has been blocked. If you have any questions, please contact support at admin@onlystatsvip.com';
        }
      }
      
      // If blocked, don't proceed
      if (isBlocked) {
        const errorMessage = blockedErrorMessage || 'Your account has been blocked. If you have any questions, please contact support at admin@onlystatsvip.com';
        setError(errorMessage);
        // Don't show toast - the Alert (from setError) is sufficient
        await logoutUser();
        setLoading(false);
        return; // CRITICAL: Don't navigate, stay on login page
      }
      
      // Email verification handled via OTP during registration, not Firebase
      toast.success('Login successful!');
      
      // Set flag to trigger navigation when authData becomes available
      // This prevents race condition where navigation happens before AuthContext updates
      setPendingNavigation(true);
      
      // Also try immediate navigation as fallback (in case authData is already set)
      // The useEffect will handle it if authData isn't ready yet
      if (authData?.access_token) {
        setPendingNavigation(false);
        navigate('/dashboard');
      } else {
        // Fallback: navigate after a short delay if authData still isn't available
        setTimeout(() => {
          if (pendingNavigation) {
            setPendingNavigation(false);
            navigate('/dashboard');
          }
        }, 1500);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.code === 'auth/invalid-credential' 
        ? 'Invalid email or password.'
        : error.code === 'auth/too-many-requests'
        ? 'Too many failed attempts. Please try again later.'
        : 'Login failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 8 }}>
      <Card elevation={3}>
        <CardContent>
          <Typography variant="h5" component="h1" align="center" gutterBottom>
            Log In
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                fullWidth
                autoFocus
              />

              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                fullWidth
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
              
              <Link 
                component={RouterLink} 
                to="/forgot-password"
                sx={{ textAlign: 'center', display: 'block', mt: 1 }}
              >
                Forgot password?
              </Link>
            </Stack>
          </Box>

          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            Don't have an account?{' '}
            <Link component={RouterLink} to="/register">
              Register here
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
}
